import type { SequenceModel } from './model';
import type { GeoEdge, GeoFrame, GeoItem, GeoNode, Geometry, Measure } from './geometry';
import { finish } from './layout-flow';
import { THEME } from './theme';

/** Merge overlapping [y0,y1] intervals and subtract them from [top,bottom]. */
function visibleSegments(top: number, bottom: number, blocked: [number, number][]): [number, number][] {
  if (bottom <= top) return [];
  const occ = blocked
    .map(([a, b]) => [Math.max(top, a), Math.min(bottom, b)] as [number, number])
    .filter(([a, b]) => b > a + 0.5)
    .sort((x, y) => x[0] - y[0]);
  const merged: [number, number][] = [];
  for (const [a, b] of occ) {
    const last = merged[merged.length - 1];
    if (!last || a > last[1]) merged.push([a, b]);
    else last[1] = Math.max(last[1], b);
  }
  const out: [number, number][] = [];
  let y = top;
  for (const [a, b] of merged) {
    if (a > y + 0.5) out.push([y, a]);
    y = Math.max(y, b);
  }
  if (y < bottom - 0.5) out.push([y, bottom]);
  return out;
}

/**
 * Sequence diagram layout (docs/ru/DSKHEMA.md §6): a column per participant, a row per event.
 * Column gaps grow so every message label fits between its ends.
 */
const HEAD_H = 42;
const ACTOR_H = 64;
const MIN_GAP = 150;
const ROW = 40;
const ACT_W = 10;
const LINE_H = 1.3;

export interface SequenceLabels {
  /** Tag text of blocks, e.g. loop → "цикл". */
  block: Record<'loop' | 'alt' | 'opt' | 'par', string>;
}

const EN_LABELS: SequenceLabels = { block: { loop: 'loop', alt: 'alt', opt: 'opt', par: 'par' } };

export function layoutSequence(model: SequenceModel, measure: Measure, labels: SequenceLabels = EN_LABELS): Geometry {
  const font = THEME.font;
  const small = THEME.smallFont;
  const ps = model.participants;
  const col = new Map(ps.map((p, i) => [p.id, i]));
  const textW = (s: string, f: number = small) => Math.max(0, ...s.split('\n').map((l) => measure(l, f, false)));
  const textH = (s: string, f: number = small) => s.split('\n').length * f * LINE_H;
  const boxW = ps.map((p) => Math.max(100, textW(p.label, font) + 28));

  // numbering is part of the label
  let num = 0;
  const msgLabel = new Map<number, string>();
  model.events.forEach((e, i) => {
    if (e.t !== 'message') return;
    num++;
    msgLabel.set(i, model.autonumber ? `${num}. ${e.label}`.trim() : e.label);
  });

  // ---- column centers: default spacing, widened for labels
  const gaps = ps.map((_, i) => (i === 0 ? 0 : Math.max(MIN_GAP, (boxW[i - 1] + boxW[i]) / 2 + 30)));
  const need: { a: number; b: number; w: number }[] = [];
  let selfRoom = 0;
  model.events.forEach((e, i) => {
    if (e.t === 'message') {
      const a = col.get(e.from)!;
      const b = col.get(e.to)!;
      const w = textW(msgLabel.get(i) ?? '') + 30;
      if (a === b) {
        if (a === ps.length - 1) selfRoom = Math.max(selfRoom, w + 30);
        else need.push({ a, b: a + 1, w: w + 40 });
      } else need.push({ a: Math.min(a, b), b: Math.max(a, b), w });
    } else if (e.t === 'note' && e.who.length === 1) {
      const c = col.get(e.who[0])!;
      const w = textW(e.text) + 40;
      if (e.place === 'right' && c < ps.length - 1) need.push({ a: c, b: c + 1, w: w + 20 });
      if (e.place === 'left' && c > 0) need.push({ a: c - 1, b: c, w: w + 20 });
    }
  });
  need.sort((x, y) => x.b - x.a - (y.b - y.a));
  for (const n of need) {
    let span = 0;
    for (let k = n.a + 1; k <= n.b; k++) span += gaps[k];
    if (span < n.w) {
      const extra = (n.w - span) / (n.b - n.a);
      for (let k = n.a + 1; k <= n.b; k++) gaps[k] += extra;
    }
  }
  const cx: number[] = [];
  let x = boxW[0] / 2 + 40;
  ps.forEach((_, i) => {
    x += gaps[i];
    cx.push(x);
  });

  const items: GeoItem[] = [];
  const headH = ps.some((p) => p.actor) ? ACTOR_H : HEAD_H;
  ps.forEach((p, i) => {
    const node: GeoNode = {
      k: 'node',
      id: p.id,
      shape: p.actor ? 'actor' : 'participant',
      x: cx[i] - boxW[i] / 2,
      y: 0,
      w: boxW[i],
      h: headH,
      label: p.label,
      fill: THEME.participant.fill,
      stroke: THEME.participant.stroke,
      text: THEME.node.text,
      width: 1.5,
      strokeDash: 'solid',
      fontSize: font,
      bold: true,
    };
    items.push(node);
  });

  // ---- rows
  let y = headH + 26;
  const lanes: GeoItem[] = [];
  const fg: GeoItem[] = [];
  const active = ps.map(() => [] as number[]);
  const bars: GeoNode[] = [];
  const frames: { frame: GeoFrame; involved: Set<number>; depth: number }[] = [];
  const openFrames: { frame: GeoFrame; involved: Set<number>; depth: number }[] = [];
  const edgeX = (c: number, toward: number) => {
    const d = active[c].length;
    if (!d) return cx[c];
    const off = ACT_W / 2 + (d - 1) * (ACT_W / 2);
    return toward >= cx[c] ? cx[c] + off : cx[c] - off;
  };
  const activate = (c: number, at: number) => active[c].push(at);
  const deactivate = (c: number, at: number) => {
    const start = active[c].pop();
    if (start === undefined) return;
    const d = active[c].length;
    bars.push({
      k: 'node',
      id: `act-${bars.length}`,
      shape: 'activation',
      x: cx[c] - ACT_W / 2 + d * (ACT_W / 2),
      y: start,
      w: ACT_W,
      h: Math.max(10, at - start),
      label: '',
      fill: THEME.activation.fill,
      stroke: THEME.activation.stroke,
      text: THEME.node.text,
      width: 1.2,
      strokeDash: 'solid',
      fontSize: small,
    });
  };
  const involve = (...cs: number[]) => {
    for (const f of openFrames) for (const c of cs) f.involved.add(c);
  };

  model.events.forEach((e, i) => {
    switch (e.t) {
      case 'message': {
        const a = col.get(e.from)!;
        const b = col.get(e.to)!;
        involve(a, b);
        const label = msgLabel.get(i) ?? '';
        const lh = label ? textH(label) : 0;
        y += lh;
        if (a === b) {
          const x0 = edgeX(a, cx[a] + 1);
          const w = 36;
          const pts = [x0, y, x0 + w, y, x0 + w, y + 22, edgeX(a, cx[a] + 1) + (e.activate ? ACT_W / 2 : 0), y + 22];
          const edge = messageEdge(e.kind, pts);
          if (label) edge.label = { text: label, x: x0 + w + 6, y: y - lh / 2 + 4, w: textW(label) + 8, h: lh, fontSize: small, color: THEME.edge.label, plain: true };
          fg.push(edge);
          if (e.activate) activate(b, y + 22);
          if (e.deactivate) deactivate(a, y + 22);
          y += 22 + ROW * 0.6;
          break;
        }
        if (e.activate) activate(b, y);
        const x0 = edgeX(a, cx[b]);
        if (e.deactivate) deactivate(a, y);
        const x1 = edgeX(b, cx[a]);
        const edge = messageEdge(e.kind, [x0, y, x1, y]);
        if (label) {
          const w = textW(label) + 8;
          edge.label = { text: label, x: (x0 + x1) / 2 - w / 2, y: y - lh - 3, w, h: lh, fontSize: small, color: THEME.edge.label, plain: true };
        }
        fg.push(edge);
        y += ROW * 0.75;
        break;
      }
      case 'activate':
        activate(col.get(e.who)!, y - ROW * 0.35);
        involve(col.get(e.who)!);
        break;
      case 'deactivate':
        deactivate(col.get(e.who)!, y - ROW * 0.35);
        break;
      case 'note': {
        const cs = e.who.map((w) => col.get(w)!);
        involve(...cs);
        const tw = textW(e.text) + 20;
        const th = textH(e.text) + 14;
        let nx: number;
        let nw = tw;
        if (e.place === 'over') {
          const lo = Math.min(...cs.map((c) => cx[c]));
          const hi = Math.max(...cs.map((c) => cx[c]));
          nw = Math.max(tw, hi - lo + 60);
          nx = (lo + hi) / 2 - nw / 2;
        } else if (e.place === 'left') nx = cx[cs[0]] - 14 - nw;
        else nx = cx[cs[0]] + 14;
        fg.push({ k: 'node', id: `note-${i}`, shape: 'note', x: nx, y, w: nw, h: th, label: e.text, fill: THEME.note.fill, stroke: THEME.note.stroke, text: THEME.note.text, width: 1.2, strokeDash: 'solid', fontSize: small });
        y += th + 14;
        break;
      }
      case 'block': {
        const depth = openFrames.length;
        const frame: GeoFrame = { k: 'frame', tag: labels.block[e.kind], label: e.label ? `[${e.label}]` : '', x: 0, y: y - 8, w: 0, h: 0, sections: [], stroke: THEME.frame.stroke, fontSize: small };
        const rec = { frame, involved: new Set<number>(), depth };
        openFrames.push(rec);
        frames.push(rec);
        y += 30;
        break;
      }
      case 'section': {
        const top = openFrames[openFrames.length - 1];
        if (top) top.frame.sections.push({ y: y - 6, label: e.label ? `[${e.label}]` : '' });
        y += 28;
        break;
      }
      case 'end': {
        const rec = openFrames.pop();
        if (rec) {
          rec.frame.h = y - rec.frame.y;
          y += 14;
        }
        break;
      }
    }
  });
  for (const rec of openFrames) rec.frame.h = y - rec.frame.y;
  // close what is still active at the bottom
  ps.forEach((_, c) => {
    while (active[c].length) deactivate(c, y);
  });
  // ---- frames span the participants they touch
  const leftAll = cx[0] - boxW[0] / 2 - 10;
  const rightAll = cx[cx.length - 1] + boxW[boxW.length - 1] / 2 + 10 + selfRoom;
  for (const rec of frames) {
    const cs = [...rec.involved];
    const inset = rec.depth * 10;
    const lo = cs.length ? Math.min(...cs.map((c) => cx[c])) - 70 : leftAll;
    const hi = cs.length ? Math.max(...cs.map((c) => cx[c])) + 70 : rightAll;
    const tagW = measure(rec.frame.tag, small, true) + 18 + measure(rec.frame.label, small, false) + 20;
    rec.frame.x = lo + inset;
    rec.frame.w = Math.max(hi - lo - inset * 2, tagW);
  }

  // ---- lifelines (split around activations, notes, texts and block dividers on the column)
  const noteBoxes = fg.filter((it): it is GeoNode => it.k === 'node' && it.shape === 'note');
  // [x0, x1, y0, y1]: a lifeline never runs through text
  const textBoxes: [number, number, number, number][] = [];
  const PAD = 3;
  for (const it of fg) {
    if (it.k === 'edge' && it.label) {
      const l = it.label;
      textBoxes.push([l.x - PAD, l.x + l.w + PAD, l.y - PAD, l.y + l.h + PAD]);
    }
  }
  for (const { frame: f } of frames) {
    // tag width as drawn by primitives.frame / explode
    const tagW = f.tag.length * f.fontSize * 0.62 + 16;
    textBoxes.push([f.x, f.x + tagW + PAD, f.y, f.y + 22 + PAD]);
    if (f.label) {
      const lx = f.x + tagW + 8;
      textBoxes.push([lx - PAD, lx + measure(f.label, small, false) + PAD, f.y, f.y + 22]);
    }
    for (const s of f.sections) {
      // the divider crosses the lifeline with a gap, its label hides it
      textBoxes.push([f.x, f.x + f.w, s.y - 4, s.y + 4]);
      if (s.label) textBoxes.push([f.x + 10 - PAD, f.x + 10 + measure(s.label, small, false) + PAD, s.y, s.y + 22]);
    }
  }
  ps.forEach((_, i) => {
    const x = cx[i];
    // the lifeline ends at the last element on its own column, never below it
    const near = (px: number) => Math.abs(px - x) <= ACT_W * 2;
    let bottom = headH + 20;
    for (const it of fg) {
      if (it.k === 'edge') {
        const n = it.points.length;
        if (near(it.points[0])) bottom = Math.max(bottom, it.points[1]);
        if (near(it.points[n - 2])) bottom = Math.max(bottom, it.points[n - 1]);
      } else if (it.k === 'node' && x >= it.x && x <= it.x + it.w) bottom = Math.max(bottom, it.y + it.h);
    }
    for (const bar of bars) if (x >= bar.x - 0.5 && x <= bar.x + bar.w + 0.5) bottom = Math.max(bottom, bar.y + bar.h);
    for (const { frame } of frames) if (x >= frame.x && x <= frame.x + frame.w) bottom = Math.max(bottom, frame.y + frame.h);
    const blocked: [number, number][] = [];
    for (const bar of bars) {
      if (x >= bar.x - 0.5 && x <= bar.x + bar.w + 0.5) blocked.push([bar.y, bar.y + bar.h]);
    }
    for (const note of noteBoxes) {
      if (x >= note.x - 2 && x <= note.x + note.w + 2) blocked.push([note.y, note.y + note.h]);
    }
    for (const [x0, x1, y0, y1] of textBoxes) if (x >= x0 && x <= x1) blocked.push([y0, y1]);
    for (const [y0, y1] of visibleSegments(headH, bottom, blocked)) {
      lanes.push({ k: 'edge', points: [x, y0, x, y1], arrowStart: false, arrowEnd: false, dash: 'fine', width: 1, color: THEME.lifeline });
    }
  });
  items.push(...lanes, ...frames.map((f) => f.frame), ...bars, ...fg);
  return finish('sequence', items);
}

function messageEdge(kind: 'sync' | 'reply' | 'async', points: number[]): GeoEdge {
  return { k: 'edge', points, arrowStart: false, arrowEnd: true, openArrow: kind === 'async', dash: kind === 'reply' ? 'fine' : 'solid', width: THEME.edge.width, color: THEME.edge.color };
}
